import { Injectable } from '@angular/core';
import { GuardsCheckEnd, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

export type TourStep = 'intro' | 'location' | 'list' | 'why' | 'photos' | 'menu' | 'promos' | 'future';
export interface TourState { version: 1; step: TourStep; paused: boolean; completed: boolean; missing: boolean; }
const SESSION_KEY = 'hungery-guided-tour-v1';
const SNAPSHOT_KEY = 'hungery-guided-tour-listing-v1';
const STEPS: TourStep[] = ['intro', 'location', 'list', 'why', 'photos', 'menu', 'promos', 'future'];

@Injectable({ providedIn: 'root' })
export class TourService {
  readonly enabled = true;
  // Staging and production have separate branch identities for this restaurant.
  readonly restaurantId = environment.production ? 1 : 316;
  readonly restaurantName = 'F5TY F5VE Aniva';
  readonly slug = 'f5ty-f5ve-aniva';
  readonly location = { lat: -6.2723775, lng: 106.6203261, label: 'Aniva, Gading Serpong' };
  readonly state$ = new BehaviorSubject<TourState | null>(this.readState());
  readonly message$ = new BehaviorSubject<string>('');
  private snapshot: any = this.readSnapshot();
  private restorePending = false;

  constructor(private router: Router) {
    this.router.events.subscribe(event => {
      if (!this.active || (!(event instanceof NavigationEnd) && !(event instanceof GuardsCheckEnd && event.shouldActivate))) return;
      const path = event.urlAfterRedirects.split(/[?#]/)[0];
      const routeStep = this.stepForRoute(path);
      if (routeStep) {
        if (this.step !== routeStep) this.set({ step: routeStep, completed: false, missing: false });
      } else if (path !== this.routeFor(this.step)) {
        this.set({ paused: true });
      }
    });
  }

  get state(): TourState | null { return this.state$.value; }
  get active(): boolean { return this.enabled && !!this.state && !this.state.paused; }
  get step(): TourStep { return this.state?.step || 'intro'; }
  get reading(): boolean { return ['intro', 'why', 'future'].includes(this.step); }
  get chapter(): number { return ({ intro: 1, location: 2, list: 2, why: 3, photos: 4, menu: 4, promos: 4, future: 5 })[this.step]; }

  enterReading(step: TourStep): void {
    if (!this.enabled) { void this.router.navigateByUrl('/'); return; }
    this.message$.next('');
    if (!this.state || step === 'intro') this.commit({ version: 1, step, paused: false, completed: false, missing: false });
    else this.set({ step, paused: false, completed: false, missing: false });
  }

  go(step: TourStep): void {
    if (!this.enabled) return;
    this.message$.next('');
    this.commit({ version: 1, step, paused: false, completed: false, missing: false });
    if (this.router.url.split(/[?#]/)[0] === this.routeFor(step)) return;
    void this.router.navigateByUrl(this.routeFor(step)).then(ok => {
      if (!ok) this.message$.next('TOUR.ERROR_NAVIGATION');
    }).catch(() => this.message$.next('TOUR.ERROR_NAVIGATION'));
  }

  next(): void {
    if (this.acknowledgeLocation()) return;
    if (this.step === 'future') { this.finish(); return; }
    if (['photos', 'menu', 'promos'].includes(this.step) && !this.state?.completed && !this.state?.missing) return;
    if (this.step === 'list') return;
    this.go(STEPS[Math.min(STEPS.indexOf(this.step) + 1, STEPS.length - 1)]);
  }

  back(): void { this.go(STEPS[Math.max(0, STEPS.indexOf(this.step) - 1)]); }
  skip(): void {
    this.go(({ intro: 'location', location: 'why', list: 'why', why: 'photos', photos: 'future', menu: 'future', promos: 'future', future: 'intro' } as Record<TourStep, TourStep>)[this.step]);
  }
  retry(): void {
    this.message$.next('');
    const route = this.routeFor(this.step);
    // A same-URL navigation need not recreate a failed HTTP view. Reload deliberately,
    // retaining only our minimal versioned tour state.
    window.location.assign(route);
  }
  beginRestaurant(id: number, name: string): boolean {
    if (!this.active || this.step !== 'list' || !this.matchesRestaurant(id, name)) return false;
    this.go('why');
    return true;
  }

  matchesRestaurant(id: number, name: string): boolean {
    return Number(id) === this.restaurantId &&
      String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '') === 'f5tyf5veaniva';
  }

  acknowledgeLocation(): boolean {
    if (!this.active || this.step !== 'location') return false;
    // The demo area is already loaded. Advance the guide without navigating or
    // changing the listing, so the visitor can open the highlighted restaurant.
    this.message$.next('');
    this.set({ step: 'list', completed: false, missing: false });
    return true;
  }

  notify(event: string): void {
    if (!this.active) return;
    if (event === 'listing-ready' && ['location', 'list'].includes(this.step)) { this.message$.next(''); this.set({ missing: false }); }
    if (event === 'promos-error' && this.step === 'promos') {
      this.set({ missing: true });
      this.message$.next('TOUR.ERROR_PROMOS');
    }
    if (event === 'restaurant-unavailable' && this.step === 'list') this.set({ missing: true });
    if (event === `${this.step}-unavailable`) this.set({ missing: true, completed: false });
    if (event === `${this.step}-ready`) { this.set({ missing: false }); this.message$.next(''); }
    if (['photos', 'menu'].includes(this.step) && event === `${this.step}-explored`) this.set({ completed: true, missing: false });
    if (event === 'promo-opened' && this.step === 'promos') this.set({ completed: true });
  }

  pause(): void {
    this.set({ paused: true });
    this.restorePending = true;
    window.location.assign('/listing');
  }
  resume(): void {
    this.set({ paused: false, completed: false, missing: false });
    window.location.assign(this.routeFor(this.step));
  }
  finish(): void {
    this.restorePending = true;
    this.state$.next(null);
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    window.location.assign('/listing');
  }
  dismiss(): void {
    this.state$.next(null);
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  }

  snapshotListing(value: any): void {
    if (this.snapshot != null) return;
    // Listing integrations pass a projection of filter/viewport settings, never API records.
    this.snapshot = value == null ? {} : value;
    try { sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(this.snapshot)); } catch {}
  }
  restoreListingSnapshot(): any {
    if (this.active && !this.restorePending) return null;
    this.restorePending = false;
    const value = this.snapshot;
    this.snapshot = null;
    try { sessionStorage.removeItem(SNAPSHOT_KEY); } catch {}
    return value;
  }

  routeFor(step: TourStep): string {
    if (step === 'intro') return '/tour';
    if (step === 'why') return '/tour/why-directory';
    if (step === 'future') return '/tour/what-comes-next';
    if (step === 'location' || step === 'list') return '/listing';
    if (step === 'photos' || step === 'menu') return `/business-detail/${this.restaurantId}`;
    return `/mini-menu/${this.slug}`;
  }
  private stepForRoute(path: string): TourStep | null {
    if (path === '/tour' || path === '/tour/introduction') return 'intro';
    if (path === '/tour/why-directory') return 'why';
    if (path === '/tour/what-comes-next') return 'future';
    if (path === '/listing' && !['location', 'list'].includes(this.step)) return 'list';
    if (path === `/business-detail/${this.restaurantId}` && !['photos', 'menu'].includes(this.step)) return 'photos';
    if (path === `/mini-menu/${this.slug}`) return 'promos';
    return null;
  }
  private set(change: Partial<TourState>): void {
    if (!this.state) return;
    const state = { ...this.state, ...change };
    if (JSON.stringify(state) !== JSON.stringify(this.state)) this.commit(state);
  }
  private commit(state: TourState): void {
    this.state$.next(state);
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(state)); } catch {}
  }
  private readState(): TourState | null {
    if (!this.enabled) return null;
    try {
      const value = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
      if (value?.version !== 1 || !STEPS.includes(value.step)) return null;
      return { version: 1, step: value.step, paused: value.paused === true, completed: false, missing: false };
    } catch { return null; }
  }
  private readSnapshot(): any {
    try { return JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || 'null'); } catch { return null; }
  }
}
